import React, { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../../api";
import moment from "moment-timezone";
import "../../views/BookingFlow/BookingFlow.css";
import ConciergeLogo from "../../assets/images/conciergeLogoLight.svg";
import Modal from "../common/Modal";
import {
  selectedBookingDateToIso,
  PUBLIC_HOLIDAY_SURCHARGE_NOTICE,
  PUBLIC_HOLIDAY_CALENDAR_LEGEND,
  publicHolidaysQueryForLocation,
} from "../../utils/bookingFlowPublicHolidays";
import { formatArrivalTime } from "../../utils/formatArrivalTime";

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Helper function to calculate distance between two coordinates (Haversine formula)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radius of the Earth in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  return distance;
};

const Step1 = ({
  cartData,
  onStepComplete,
  onCartUpdate,
  isLoading = false,
}) => {
  const [email, setEmail] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  // Initialize with formatted "Today" date so it matches the date button comparison
  const today = useRef(moment().format("dddd, MMMM Do YYYY"));
  const tomorrow = useRef(moment().add(1, "day").format("dddd, MMMM Do YYYY"));
  const dayAfterTomorrow = moment().add(2, "day");
  const [selectedDate, setSelectedDate] = useState(today.current);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [activeTimeTab, setActiveTimeTab] = useState(null); // Start with none open
  const [timeSlots, setTimeSlots] = useState({
    morning: [],
    afternoon: [],
    evening: [],
  });
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [vehicleTypeError, setVehicleTypeError] = useState("");
  const [locationError, setLocationError] = useState("");
  const [dateError, setDateError] = useState("");
  const [timeSlotError, setTimeSlotError] = useState("");
  const hasAutoOpenedAccordionRef = useRef(false); // Track if we've auto-opened accordion on initial load
  const hasInitializedFromCartRef = useRef(false); // Track if we've initialized from cart data
  const cartTimeSlotRef = useRef(null); // Store cart time slot to set it after other state updates
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [locationSearch, setLocationSearch] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showNoResults, setShowNoResults] = useState(false);
  const [isLocationInputFocused, setIsLocationInputFocused] = useState(false);
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [displayedMonth, setDisplayedMonth] = useState(moment());
  const [pendingOfferExpiryCheck, setPendingOfferExpiryCheck] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [closestLocations, setClosestLocations] = useState([]);
  const [showLocationInput, setShowLocationInput] = useState(false);
  const [isLoadingUserLocation, setIsLoadingUserLocation] = useState(false);
  const [locationCapacity, setLocationCapacity] = useState(null); // Store location capacity data
  const [publicHolidayByDate, setPublicHolidayByDate] = useState({});

  // Modal state
  const [modal, setModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
    showCancel: false,
    onConfirm: null,
  });

  // Offer modal state
  const [offerModal, setOfferModal] = useState({
    isOpen: false,
    offer: null,
  });

  // Debounced search value
  const debouncedSearch = useDebounce(locationSearch, 500);

  // Modal helper function
  const showModal = (
    title,
    message,
    type = "info",
    showCancel = false,
    onConfirm = null,
  ) => {
    setModal({
      isOpen: true,
      title,
      message,
      type,
      showCancel,
      onConfirm,
    });
  };

  const closeModal = () => {
    setModal((prev) => ({ ...prev, isOpen: false }));
  };

  // Function to clear cart cookie
  const clearCartCookie = () => {
    document.cookie =
      "ccw_booking_cart_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    console.log("Cart cookie cleared due to 404 error");
  };

  // Cookie utility functions
  const setCookie = (name, value, days) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  };

  const getCookie = (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === " ") c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  };

  // Vehicle type options
  const vehicleTypes = ["Sedan", "Wagon / SUV", "4WD / 7 Seater", "RV"];

  // Date options - dynamically calculated based on current date
  // Note: today and tomorrow are already defined above, this is just for the dateOptions array

  const dateOptions = [
    {
      value: "Today",
      label: "Today",
      dateToCheck: moment().format("YYYY-MM-DD"),
    },
    {
      value: "Tomorrow",
      label: "Tomorrow",
      dateToCheck: moment().add(1, "day").format("YYYY-MM-DD"),
    },
    {
      value: dayAfterTomorrow.format("dddd Do MMMM"),
      label: dayAfterTomorrow.format("ddd Do MMM"), // "Sun 5th Oct" format
      dateToCheck: dayAfterTomorrow.format("YYYY-MM-DD"),
    },
    { value: "Pick a date", label: "Pick a date" },
  ];

  // Function to check and store coupon code
  const checkAndStoreCoupon = useCallback(
    async (couponCode, cartId) => {
      if (!couponCode || !cartId) return;

      try {
        const response = await api(`coupon/${couponCode}`, "GET");
        if (response.status === 200 && response.data.coupon !== null) {
          const coupon = response.data.coupon;
          console.log("Coupon found:", coupon);
          // Store coupon in cart
          try {
            const updateResponse = await api(
              `booking-flow/cart/${cartId}`,
              "PUT",
              {
                couponDetails: coupon,
              },
            );
            if (updateResponse.data.success && updateResponse.data.cart) {
              console.log("Coupon stored in cart, updating cart data");
              onCartUpdate(updateResponse.data.cart);
            }
          } catch (error) {
            console.error("Error storing coupon in cart:", error);
          }
        } else {
          console.log("No coupon found for code:", couponCode);
        }
      } catch (error) {
        console.error("Error checking coupon:", error);
      }
    },
    [onCartUpdate],
  );

  // Function to check if booking date is after offer expiry
  const checkOfferExpiry = useCallback((offer, bookingDate) => {
    if (!offer || !offer.endDate || !bookingDate) {
      return null; // No validation needed
    }

    // Parse the booking date
    let parsedBookingDate;
    if (bookingDate === "Today") {
      parsedBookingDate = moment().startOf("day");
    } else if (bookingDate === "Tomorrow") {
      parsedBookingDate = moment().add(1, "day").startOf("day");
    } else if (
      bookingDate.includes(",") &&
      (bookingDate.includes("st") ||
        bookingDate.includes("nd") ||
        bookingDate.includes("rd") ||
        bookingDate.includes("th"))
    ) {
      // Format like "Wednesday, October 1st 2025"
      parsedBookingDate = moment(bookingDate, "dddd, MMMM Do YYYY");
    } else {
      parsedBookingDate = moment(bookingDate, "YYYY-MM-DD");
    }

    if (!parsedBookingDate.isValid()) {
      return null; // Can't parse date, skip validation
    }

    // Parse offer end date
    const offerEndDate = moment(offer.endDate).endOf("day");

    // Check if booking date is after offer expiry
    if (parsedBookingDate.isAfter(offerEndDate)) {
      return {
        isValid: false,
        offerEndDate: offerEndDate.format("MMMM Do, YYYY"),
        bookingDate: parsedBookingDate.format("MMMM Do, YYYY"),
      };
    }

    return { isValid: true };
  }, []);

  // Function to check offer code and display modal
  const checkAndDisplayOffer = useCallback(
    async (offerCode, cartId) => {
      try {
        const response = await api(`offer/?code=${offerCode}`, "GET");
        if (response.status === 200 && response.data.offer !== null) {
          const offer = response.data.offer;
          console.log("Offer found:", offer);

          // Check if booking date is after offer expiry (if date is already selected)
          if (selectedDate) {
            const expiryCheck = checkOfferExpiry(offer, selectedDate);
            if (expiryCheck && !expiryCheck.isValid) {
              // Show warning modal
              showModal(
                "Offer Expiry Notice",
                `The offer code "${offerCode}" expires on ${expiryCheck.offerEndDate}. Your selected booking date (${expiryCheck.bookingDate}) is after the offer expiry date, so the offer will not apply to this booking.`,
                "warning",
              );
            }
          }

          // Store offer in cart
          try {
            const updateResponse = await api(
              `booking-flow/cart/${cartId}`,
              "PUT",
              {
                offerDetails: offer,
              },
            );
            if (updateResponse.data.success && updateResponse.data.cart) {
              console.log("Offer stored in cart, updating cart data");
              // Update cart data so it's available in Step2
              onCartUpdate(updateResponse.data.cart);
            }
          } catch (error) {
            console.error("Error storing offer in cart:", error);
          }
          // Display offer modal
          setOfferModal({
            isOpen: true,
            offer: offer,
          });
        } else {
          console.log("No offer found for code:", offerCode);
        }
      } catch (error) {
        console.error("Error checking offer:", error);
      }
    },
    [onCartUpdate, selectedDate, checkOfferExpiry, showModal],
  );

  // Function to check URL params for codes on any step
  const checkUrlParamsForCodes = useCallback(
    async (cartId) => {
      if (!cartId) return;

      const queryParams = new URLSearchParams(window.location.search);
      const offerCode = queryParams.get("offer");
      const couponCode = queryParams.get("coupon");
      const rewardCode = queryParams.get("reward");

      // Check offer code
      if (
        offerCode &&
        (!cartData?.offerDetails || cartData.offerDetails.code !== offerCode)
      ) {
        await checkAndDisplayOffer(offerCode, cartId);
      }

      // Check coupon code
      if (
        couponCode &&
        (!cartData?.couponDetails || cartData.couponDetails.code !== couponCode)
      ) {
        await checkAndStoreCoupon(couponCode, cartId);
      }

      // TODO: Add reward code check if needed
    },
    [cartData, checkAndDisplayOffer, checkAndStoreCoupon],
  );

  const createInitialCart = useCallback(async () => {
    try {
      // Check for existing cart in cookies
      const existingCartId = getCookie("ccw_booking_cart_id");

      if (existingCartId) {
        // Try to fetch existing cart
        try {
          const response = await api(
            `booking-flow/cart/${existingCartId}`,
            "GET",
          );
          if (response.data.success && response.data.cart) {
            onCartUpdate(response.data.cart);
            // Check for offer code in URL even if cart exists
            const queryParams = new URLSearchParams(window.location.search);
            const offerCode = queryParams.get("offer");
            if (offerCode && !response.data.cart.offerDetails) {
              // Only check offer if cart doesn't already have offer details
              checkAndDisplayOffer(offerCode, existingCartId);
            }
            return;
          }
        } catch (error) {
          console.log("Existing cart not found, creating new one");
        }
      }

      // Capture UTM data and offer codes from URL parameters
      const queryParams = new URLSearchParams(window.location.search);
      const utmData = {
        source: queryParams.get("utm_source"),
        medium: queryParams.get("utm_medium"),
        campaign: queryParams.get("utm_campaign"),
        term: queryParams.get("utm_term"),
        content: queryParams.get("utm_content"),
      };

      // Only include utmData if at least one UTM parameter exists
      const hasUtmData = Object.values(utmData).some((val) => val !== null);

      const offerCode = queryParams.get("offer");
      const couponCode = queryParams.get("coupon");
      const rewardCode = queryParams.get("reward");

      // Build cart creation payload
      const cartPayload = {};

      if (hasUtmData) {
        cartPayload.utmData = utmData;
      }

      if (offerCode) {
        cartPayload.offerCode = offerCode;
      }

      if (couponCode) {
        cartPayload.couponCode = couponCode;
      }

      if (rewardCode) {
        cartPayload.rewardCode = rewardCode;
      }

      // Create new cart with UTM data and offer codes
      const response = await api("booking-flow/cart", "POST", cartPayload);
      if (response.data.success && response.data.cart) {
        onCartUpdate(response.data.cart);
        // Save cart ID to cookies for 2 days
        setCookie("ccw_booking_cart_id", response.data.cart._id, 2);

        // Check for offer code and display modal if valid
        if (offerCode) {
          checkAndDisplayOffer(offerCode, response.data.cart._id);
        }
      }
    } catch (error) {
      console.error("Error creating initial cart:", error);
    }
  }, [onCartUpdate, checkAndDisplayOffer]);

  const loadAllLocations = useCallback(async () => {
    try {
      console.log("Loading all locations...");
      const response = await api("location-optimized/locations", "GET");
      console.log("Locations response:", response);

      if (response.data.success && response.data.locations) {
        console.log("Loaded locations:", response.data.locations.length);
        const normalizedLocations = response.data.locations.map((location) => {
          const normalizedId =
            location.id || location._id || location.locationId;
          return {
            ...location,
            id: normalizedId,
            _id: normalizedId,
            // Explicitly preserve closureRules and closedDates
            closureRules: location.closureRules || [],
            closedDates: location.closedDates || {},
          };
        });

        // Debug: Log closure rules for first location
        if (normalizedLocations.length > 0) {
          const firstLoc = normalizedLocations[0];
          console.log("📍 Sample location closure data:", {
            name: firstLoc.name || firstLoc.seoName,
            closureRulesCount: (firstLoc.closureRules || []).length,
            closureRules: firstLoc.closureRules,
            hasClosedDates: !!firstLoc.closedDates,
          });
        }

        setLocations(normalizedLocations);
        return normalizedLocations; // Return locations for use in getUserLocation
      } else {
        console.log("No locations found:", response.data);
        return [];
      }
    } catch (error) {
      console.error("Error loading locations:", error);
      return [];
    }
  }, []);

  // Get user IP address from browser
  const getUserIpAddress = useCallback(async () => {
    try {
      // Helper function to fetch with timeout
      const fetchWithTimeout = (url, timeout = 3000) => {
        return Promise.race([
          fetch(url, {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          }),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Timeout")), timeout),
          ),
        ]);
      };

      // Try multiple IP detection services (free, no API keys needed)
      const services = [
        { url: "https://api.ipify.org?format=json", parser: (data) => data.ip },
        { url: "https://ipapi.co/json/", parser: (data) => data.ip },
        {
          url: "https://api.ip.sb/ip",
          parser: (data) => (typeof data === "string" ? data.trim() : data),
        },
      ];

      for (const service of services) {
        try {
          const response = await fetchWithTimeout(service.url, 3000);

          if (response.ok) {
            // Handle text response (api.ip.sb returns plain text)
            const contentType = response.headers.get("content-type");
            let data;
            if (contentType && contentType.includes("application/json")) {
              data = await response.json();
            } else {
              const text = await response.text();
              data = text.trim();
            }

            // Parse IP using service-specific parser
            const ip = service.parser(data);

            // Validate IP format
            if (
              ip &&
              typeof ip === "string" &&
              /^\d+\.\d+\.\d+\.\d+$/.test(ip)
            ) {
              console.log("User IP detected:", ip, "from", service.url);
              return ip;
            }
          }
        } catch (error) {
          console.log(
            `IP detection service ${service.url} failed:`,
            error.message,
          );
          continue;
        }
      }

      console.log("Could not detect user IP address");
      return null;
    } catch (error) {
      console.error("Error getting user IP:", error);
      return null;
    }
  }, []);

  // Get user location from MaxMind via backend
  const getUserLocation = useCallback(async () => {
    setIsLoadingUserLocation(true);
    try {
      // First, get the user's IP address
      const userIp = await getUserIpAddress();

      if (!userIp) {
        console.log("Could not get user IP, skipping location detection");
        return null;
      }

      // Send IP to backend to get location from MaxMind
      const response = await api("location-optimized/user-location", "POST", {
        ip: userIp,
      });
      console.log("User location response:", response);

      if (response.data.success && response.data.data) {
        const { latitude, longitude } = response.data.data;
        setUserLocation({ lat: latitude, lng: longitude });
        console.log("User location set:", { lat: latitude, lng: longitude });
        return { lat: latitude, lng: longitude };
      } else {
        console.log("Could not get user location:", response.data.message);
        return null;
      }
    } catch (error) {
      console.error("Error getting user location:", error);
      return null;
    } finally {
      setIsLoadingUserLocation(false);
    }
  }, [getUserIpAddress]);

  // Calculate distances and get closest locations
  const calculateClosestLocations = useCallback((userCoords, allLocations) => {
    if (!userCoords || !allLocations || allLocations.length === 0) {
      return [];
    }

    // Calculate distance for each location
    const locationsWithDistance = allLocations.map((location) => {
      // Support both nested coordinates and direct lat/lng
      const lat = location.coordinates?.lat || location.lat;
      const lng = location.coordinates?.lng || location.lng;

      if (lat && lng) {
        const distance = calculateDistance(
          userCoords.lat,
          userCoords.lng,
          lat,
          lng,
        );
        return {
          ...location,
          lat: lat, // Ensure lat/lng are at top level for consistency
          lng: lng,
          distance: Math.round(distance * 10) / 10, // Round to 1 decimal place
        };
      }
      return { ...location, distance: null };
    });

    // Filter out locations without coordinates and sort by distance
    const validLocations = locationsWithDistance
      .filter((loc) => loc.distance !== null)
      .sort((a, b) => a.distance - b.distance);

    // Get top 3 closest
    const top3 = validLocations.slice(0, 3);
    console.log("Top 3 closest locations:", top3);
    return top3;
  }, []);

  const getPostcodeCoordinates = useCallback(async (postcode) => {
    try {
      console.log(`Getting coordinates for postcode: ${postcode}`);

      // Try multiple geocoding services (all free, no API keys required)
      const services = [
        {
          url: `https://nominatim.openstreetmap.org/search?postalcode=${postcode}&countrycodes=au&format=json&limit=1`,
          parser: (data) => {
            if (data && data.length > 0) {
              return {
                lat: parseFloat(data[0].lat),
                lng: parseFloat(data[0].lon),
              };
            }
            return null;
          },
        },
        {
          url: `https://api.geonames.org/postalCodeSearchJSON?postalcode=${postcode}&country=AU&maxRows=1&username=demo`,
          parser: (data) => {
            if (data.postalCodes && data.postalCodes.length > 0) {
              return {
                lat: parseFloat(data.postalCodes[0].lat),
                lng: parseFloat(data.postalCodes[0].lng),
              };
            }
            return null;
          },
        },
      ];

      for (const service of services) {
        try {
          console.log(`Trying geocoding service: ${service.url}`);
          const response = await fetch(service.url);

          if (!response.ok) {
            console.log(
              `Service returned ${response.status}: ${response.statusText}`,
            );
            continue;
          }

          const data = await response.json();
          const coords = service.parser(data);

          if (
            coords &&
            coords.lat &&
            coords.lng &&
            coords.lat !== 0 &&
            coords.lng !== 0
          ) {
            console.log(
              `Postcode coordinates found: ${coords.lat}, ${coords.lng} from ${service.url}`,
            );
            return {
              lat: coords.lat,
              lng: coords.lng,
              postcode: postcode,
            };
          }
        } catch (error) {
          console.log(
            `Geocoding service ${service.url} failed:`,
            error.message,
          );
          continue;
        }
      }

      // Fallback: Try to use a simple lookup for common Australian postcodes
      const fallbackCoords = getFallbackPostcodeCoords(postcode);
      if (fallbackCoords) {
        console.log(
          `Using fallback coordinates for postcode ${postcode}:`,
          fallbackCoords,
        );
        return fallbackCoords;
      }

      // All services failed
      console.log("All geocoding services failed for postcode:", postcode);
      return null;
    } catch (error) {
      console.error("Postcode geocoding error:", error);
      return null;
    }
  }, []);

  // Fallback function for common Australian postcodes
  const getFallbackPostcodeCoords = (postcode) => {
    const commonPostcodes = {
      3064: { lat: -37.7871, lng: 145.1234, name: "Doncaster East" },
      3108: { lat: -37.7833, lng: 145.1167, name: "Doncaster" },
      3000: { lat: -37.8136, lng: 144.9631, name: "Melbourne CBD" },
      3004: { lat: -37.85, lng: 144.98, name: "St Kilda" },
      3141: { lat: -37.85, lng: 144.98, name: "Richmond" },
      3121: { lat: -37.8, lng: 145.0, name: "Hawthorn" },
      3168: { lat: -37.85, lng: 145.0, name: "Malvern" },
      3182: { lat: -37.85, lng: 144.95, name: "South Melbourne" },
    };

    const coords = commonPostcodes[postcode];
    if (coords) {
      return {
        lat: coords.lat,
        lng: coords.lng,
        postcode: postcode,
        name: coords.name,
      };
    }
    return null;
  };

  // Get suburb coordinates from backend
  const getSuburbCoordinates = useCallback(async (suburb) => {
    try {
      const response = await api(
        "location-optimized/suburb-coordinates",
        "POST",
        {
          suburb: suburb,
          state: "VIC", // Default to Victoria, can be made dynamic if needed
          country: "AU",
        },
      );

      if (response.data.success && response.data.data) {
        const { latitude, longitude } = response.data.data;
        console.log(
          `Suburb coordinates found: ${latitude}, ${longitude} for ${suburb}`,
        );
        return {
          lat: latitude,
          lng: longitude,
          suburb: suburb,
        };
      } else {
        console.log("Could not find coordinates for suburb:", suburb);
        return null;
      }
    } catch (error) {
      console.error("Error getting suburb coordinates:", error);
      return null;
    }
  }, []);

  const searchLocations = useCallback(
    async (searchTerm) => {
      if (!searchTerm || searchTerm.length < 2) {
        setFilteredLocations([]);
        setShowNoResults(false);
        return;
      }

      setIsSearching(true);
      setShowNoResults(false);

      try {
        console.log("Searching locations for:", searchTerm);

        // Check if search term is a postcode (4 digits)
        const isPostcode = /^\d{4}$/.test(searchTerm);

        let filtered = [];

        if (isPostcode) {
          // Get postcode coordinates for distance calculation
          const postcodeCoords = await getPostcodeCoordinates(searchTerm);

          if (!postcodeCoords) {
            console.log("Could not find coordinates for postcode:", searchTerm);
            setFilteredLocations([]);
            return;
          }

          console.log("Postcode coordinates:", postcodeCoords);
          console.log("Total locations available:", locations.length);

          // First try: Filter locations within 50km radius of postcode
          filtered = locations
            .filter((location) => {
              if (
                !location.coordinates ||
                !location.coordinates.lat ||
                !location.coordinates.lng
              ) {
                console.log("Location missing coordinates:", location.name);
                return false;
              }

              const distance = calculateDistance(
                postcodeCoords.lat,
                postcodeCoords.lng,
                location.coordinates.lat,
                location.coordinates.lng,
              );

              console.log(
                `Distance from ${searchTerm} to ${
                  location.name
                }: ${distance.toFixed(2)}km`,
              );
              return distance <= 50; // 50km radius
            })
            .map((location) => {
              const distance = calculateDistance(
                postcodeCoords.lat,
                postcodeCoords.lng,
                location.coordinates.lat,
                location.coordinates.lng,
              );
              return { ...location, distance: Math.round(distance * 10) / 10 };
            })
            .sort((a, b) => a.distance - b.distance); // Sort by distance

          console.log(
            "Found locations within 50km of postcode:",
            filtered.length,
          );

          // If no results within 50km, expand search to 100km (area search)
          if (filtered.length === 0) {
            console.log(
              "No locations found within 50km, expanding to 100km area search for postcode:",
              searchTerm,
            );

            filtered = locations
              .filter((location) => {
                const lat = location.coordinates?.lat || location.lat;
                const lng = location.coordinates?.lng || location.lng;

                if (!lat || !lng) {
                  return false;
                }

                const distance = calculateDistance(
                  postcodeCoords.lat,
                  postcodeCoords.lng,
                  lat,
                  lng,
                );

                return distance <= 100; // 100km radius for area search
              })
              .map((location) => {
                const lat = location.coordinates?.lat || location.lat;
                const lng = location.coordinates?.lng || location.lng;
                const distance = calculateDistance(
                  postcodeCoords.lat,
                  postcodeCoords.lng,
                  lat,
                  lng,
                );
                return {
                  ...location,
                  distance: Math.round(distance * 10) / 10,
                };
              })
              .sort((a, b) => a.distance - b.distance);

            console.log(
              "Found locations within 100km area of postcode:",
              filtered.length,
            );
          }

          if (filtered.length > 0) {
            console.log(
              "Closest locations:",
              filtered.slice(0, 3).map((l) => `${l.name} (${l.distance}km)`),
            );
          } else {
            console.log(
              "No locations found within 100km area of postcode:",
              searchTerm,
            );
          }
        } else {
          // First try to match by name/address (exact matches)
          const searchLower = searchTerm.toLowerCase();
          const nameMatches = locations.filter((location) => {
            // Extract suburb from address (usually after the street address, before state/postcode)
            const addressParts = location.address
              ? location.address.split(",")
              : [];
            const suburb =
              addressParts.length > 1
                ? addressParts[addressParts.length - 2]?.trim()
                : "";

            return (
              (location.seoName &&
                location.seoName.toLowerCase().includes(searchLower)) ||
              (location.name &&
                location.name.toLowerCase().includes(searchLower)) ||
              (location.address &&
                location.address.toLowerCase().includes(searchLower)) ||
              (location.city &&
                location.city.toLowerCase().includes(searchLower)) ||
              (suburb && suburb.toLowerCase().includes(searchLower))
            );
          });

          // If we found matches by name, use those
          if (nameMatches.length > 0) {
            filtered = nameMatches;
            console.log("Found locations by name/address:", filtered.length);
          } else {
            // No name matches, try geocoding as suburb
            console.log(
              "No name matches, trying suburb geocoding for:",
              searchTerm,
            );
            const suburbCoords = await getSuburbCoordinates(searchTerm);

            if (suburbCoords) {
              console.log("Suburb coordinates:", suburbCoords);
              console.log("Total locations available:", locations.length);

              // Filter locations within 50km radius of suburb
              filtered = locations
                .filter((location) => {
                  const lat = location.coordinates?.lat || location.lat;
                  const lng = location.coordinates?.lng || location.lng;

                  if (!lat || !lng) {
                    return false;
                  }

                  const distance = calculateDistance(
                    suburbCoords.lat,
                    suburbCoords.lng,
                    lat,
                    lng,
                  );

                  console.log(
                    `Distance from ${searchTerm} to ${
                      location.name
                    }: ${distance.toFixed(2)}km`,
                  );
                  return distance <= 50; // 50km radius
                })
                .map((location) => {
                  const lat = location.coordinates?.lat || location.lat;
                  const lng = location.coordinates?.lng || location.lng;
                  const distance = calculateDistance(
                    suburbCoords.lat,
                    suburbCoords.lng,
                    lat,
                    lng,
                  );
                  return {
                    ...location,
                    distance: Math.round(distance * 10) / 10,
                  };
                })
                .sort((a, b) => a.distance - b.distance);

              console.log(
                "Locations found within 50km of suburb:",
                filtered.length,
              );
            } else {
              // No suburb coordinates found, return empty
              filtered = [];
              console.log("Could not find coordinates for suburb:", searchTerm);
            }
          }
        }

        setFilteredLocations(filtered);

        // Show "no results" message only if no results found, with a small delay
        if (filtered.length === 0) {
          setTimeout(() => {
            setShowNoResults(true);
          }, 500);
        }
      } catch (error) {
        console.error("Error searching locations:", error);
        setFilteredLocations([]);
        setTimeout(() => {
          setShowNoResults(true);
        }, 500);
      } finally {
        setIsSearching(false);
      }
    },
    [locations, getPostcodeCoordinates, getSuburbCoordinates],
  );

  const loadTimeSlots = useCallback(
    async (locationId) => {
      const effectiveLocationId = locationId || selectedLocationId;
      if (!effectiveLocationId || !selectedDate) return;

      setIsLoadingTimeSlots(true);
      try {
        // Convert selected date into API-friendly format (YYYY-MM-DD)
        const possibleFormats = [
          "dddd, MMMM Do YYYY",
          "dddd Do MMMM",
          "ddd Do MMM",
          "YYYY-MM-DD",
        ];

        let actualDate;

        // Check if selectedDate is "Today" string or matches the formatted "Today" date
        const now = moment();
        const todayFormatted = now.clone().format("dddd, MMMM Do YYYY");
        const tomorrowFormatted = now
          .clone()
          .add(1, "day")
          .format("dddd, MMMM Do YYYY");

        if (selectedDate === "Today" || selectedDate === todayFormatted) {
          actualDate = now.clone().format("YYYY-MM-DD");
        } else if (
          selectedDate === "Tomorrow" ||
          selectedDate === tomorrowFormatted
        ) {
          actualDate = now.clone().add(1, "day").format("YYYY-MM-DD");
        } else {
          const parsed = moment(selectedDate, possibleFormats, true);
          actualDate = parsed.isValid()
            ? parsed.format("YYYY-MM-DD")
            : moment(selectedDate).isValid()
              ? moment(selectedDate).format("YYYY-MM-DD")
              : now.clone().format("YYYY-MM-DD");
        }

        console.log("Selected date:", selectedDate);
        console.log("Today formatted:", todayFormatted);
        console.log("Actual date being sent to API:", actualDate);

        console.log(
          "Loading time slots for location:",
          effectiveLocationId,
          "date:",
          actualDate,
        );
        const response = await api(
          `booking-flow/timeslots/${effectiveLocationId}?date=${actualDate}`,
          "GET",
        );

        if (response.data.success) {
          const groupedSlots = {
            morning: [],
            afternoon: [],
            evening: [],
          };

          const rawSlots = response.data.timeSlots;

          const pushSlot = (slot) => {
            if (!slot || !slot.timeKey) {
              return;
            }
            // Include ALL slots (even unavailable ones) - they'll be greyed out in the UI
            const hour = parseInt(slot.timeKey?.split(":")[0], 10);
            if (hour < 12) {
              groupedSlots.morning.push(slot);
            } else if (hour < 17) {
              groupedSlots.afternoon.push(slot);
            } else {
              groupedSlots.evening.push(slot);
            }
          };

          if (Array.isArray(rawSlots)) {
            rawSlots.forEach(pushSlot);
          } else if (rawSlots && typeof rawSlots === "object") {
            Object.entries(rawSlots).forEach(([key, slots]) => {
              if (Array.isArray(slots)) {
                const period = key.toLowerCase();
                if (groupedSlots[period]) {
                  groupedSlots[period] = slots;
                } else {
                  slots.forEach(pushSlot);
                }
              }
            });
          }

          setTimeSlots(groupedSlots);

          // Validate that the selected time slot still exists in loaded slots
          // If it does, keep it; if not, preserve it anyway (it might be from a different period)
          if (selectedTimeSlot && selectedTimeSlot.timeKey) {
            console.log(
              "Checking if selected time slot still valid:",
              selectedTimeSlot,
            );
            // Don't clear it - even if it doesn't match, preserve it for now
            // The user might have selected a slot that's temporarily unavailable
          }
        } else {
        }
      } catch (error) {
        console.error("Error loading time slots:", error);
      } finally {
        setIsLoadingTimeSlots(false);
      }
    },
    [selectedDate, selectedLocationId],
  );

  const handleEmailChange = async (e) => {
    const value = e.target.value;
    setEmail(value);
    // Clear error when user starts typing
    if (value && value.includes("@")) {
      setEmailError("");
    }

    // Update cart with new email if we have a cart
    if (cartData && value) {
      updateCartStep1();
    }
  };

  const updateCartStep1 = useCallback(
    async (
      overrideLocationId = null,
      locationObject = null,
      overrideTimeSlot = null,
    ) => {
      if (!cartData) return;

      try {
        const updateData = {
          vehicleType,
          selectedDate,
          selectedTimeSlot:
            overrideTimeSlot !== null ? overrideTimeSlot : selectedTimeSlot,
          selectedLocationId: overrideLocationId || selectedLocationId,
        };

        // Include complete location object if provided
        if (locationObject) {
          updateData.selectedLocation = {
            id: locationObject.id,
            name: locationObject.name,
            seoName: locationObject.seoName,
            address: locationObject.address,
            city: locationObject.city,
            coordinates: locationObject.coordinates,
            timezone: locationObject.timezone,
            closedDates: locationObject.closedDates || {}, // Include closedDates for calendar filtering
          };
        }

        // Only include email if it's provided
        if (email) {
          updateData.email = email;
        }

        console.log("Updating cart with data:", updateData);

        const response = await api(
          `booking-flow/cart/${cartData._id}/step1`,
          "PUT",
          updateData,
        );

        if (response.data.success) {
          console.log("Cart updated successfully:", response.data.cart);
          onCartUpdate(response.data.cart);
        }
      } catch (error) {
        console.error("Error updating cart step 1:", error);
      }
    },
    [
      cartData,
      vehicleType,
      selectedDate,
      selectedTimeSlot,
      selectedLocationId,
      email,
      onCartUpdate,
    ],
  );

  const handleVehicleTypeChange = (e) => {
    setVehicleType(e.target.value);
    // Clear error when vehicle type is selected
    if (e.target.value) {
      setVehicleTypeError("");
    }
    // Update cart immediately
    setTimeout(() => updateCartStep1(), 100);
  };

  const handleLocationSearchChange = (e) => {
    setLocationSearch(e.target.value);
    setShowNoResults(false); // Reset no results state when typing
    // Clear selected location when searching
    if (e.target.value !== locationSearch) {
      setSelectedLocationId(null);
      setSelectedTimeSlot(null);
      setActiveTimeTab(null);
    }
  };

  const handleLocationInputFocus = () => {
    setIsLocationInputFocused(true);
  };

  const handleLocationInputBlur = () => {
    // Delay hiding the dropdown to allow clicking on options
    setTimeout(() => {
      setIsLocationInputFocused(false);
    }, 150);
  };

  const handleLocationSelect = (location) => {
    console.log(
      "Location selected:",
      location.seoName || location.name,
      "ID:",
      location.id || location._id,
    );
    const normalizedId = location.id || location._id;
    setSelectedLocationId(normalizedId);
    // Clear location error when location is selected
    setLocationError("");
    // Use seoName for display if available, otherwise fallback to name
    setLocationSearch(location.seoName || location.name);
    setFilteredLocations([]);
    setIsLocationInputFocused(false); // Hide dropdown when location is selected
    setShowLocationInput(false); // Hide input when a location is selected from closest locations
    setSelectedTimeSlot(null);
    setActiveTimeTab(null);
    // Update cart immediately with the complete location object (including closedDates and closureRules)
    setTimeout(
      () =>
        updateCartStep1(normalizedId, {
          ...location,
          id: normalizedId,
          _id: normalizedId,
          closedDates: location.closedDates || {}, // Ensure closedDates is included
          closureRules: location.closureRules || [], // Ensure closureRules is included
        }),
      100,
    );
  };

  // Helper function to check if a date matches a closure rule
  const matchesClosureRule = useCallback((date, rule) => {
    const checkDate = moment(date);
    if (!checkDate.isValid()) {
      return false;
    }

    // Check if rule has start/end date constraints
    if (rule.startDate && checkDate.isBefore(moment(rule.startDate), "day")) {
      return false;
    }
    if (rule.endDate && checkDate.isAfter(moment(rule.endDate), "day")) {
      return false;
    }

    // Evaluate based on rule type
    switch (rule.ruleType) {
      case "firstSundayOfMonth":
        return isFirstDayOfWeekInMonth(checkDate, 0); // 0 = Sunday
      case "secondSundayOfMonth":
        return isNthDayOfWeekInMonth(checkDate, 0, 2);
      case "thirdSundayOfMonth":
        return isNthDayOfWeekInMonth(checkDate, 0, 3);
      case "lastSundayOfMonth":
        return isLastDayOfWeekInMonth(checkDate, 0);
      case "firstMondayOfMonth":
        return isFirstDayOfWeekInMonth(checkDate, 1);
      case "lastMondayOfMonth":
        return isLastDayOfWeekInMonth(checkDate, 1);
      case "firstTuesdayOfMonth":
        return isFirstDayOfWeekInMonth(checkDate, 2);
      case "lastTuesdayOfMonth":
        return isLastDayOfWeekInMonth(checkDate, 2);
      case "firstWednesdayOfMonth":
        return isFirstDayOfWeekInMonth(checkDate, 3);
      case "lastWednesdayOfMonth":
        return isLastDayOfWeekInMonth(checkDate, 3);
      case "firstThursdayOfMonth":
        return isFirstDayOfWeekInMonth(checkDate, 4);
      case "lastThursdayOfMonth":
        return isLastDayOfWeekInMonth(checkDate, 4);
      case "firstFridayOfMonth":
        return isFirstDayOfWeekInMonth(checkDate, 5);
      case "lastFridayOfMonth":
        return isLastDayOfWeekInMonth(checkDate, 5);
      case "firstSaturdayOfMonth":
        return isFirstDayOfWeekInMonth(checkDate, 6);
      case "lastSaturdayOfMonth":
        return isLastDayOfWeekInMonth(checkDate, 6);
      case "specificDayOfMonth":
        if (rule.dayOfMonth) {
          return checkDate.date() === rule.dayOfMonth;
        }
        return false;
      case "specificDateOfYear":
        // Check if both month and dayOfMonth are provided
        // moment months are 0-indexed (0-11), so we subtract 1 from rule.month
        if (rule.month && rule.dayOfMonth) {
          return (
            checkDate.month() === rule.month - 1 &&
            checkDate.date() === rule.dayOfMonth
          );
        }
        return false;
      case "dateRange":
        // startDate/endDate already validated above - if we reach here, date is in range
        return !!(rule.startDate && rule.endDate);
      default:
        return false;
    }
  }, []);

  // Helper function to check if a date is the first occurrence of a day of week in its month
  const isFirstDayOfWeekInMonth = (date, dayOfWeek) => {
    // dayOfWeek: 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    if (date.day() !== dayOfWeek) {
      return false;
    }

    // Get the first day of the month
    const firstOfMonth = date.clone().startOf("month");

    // Find the first occurrence of the target day of week in the month
    // moment().day(dayOfWeek) finds the next occurrence of that day
    // If the first day of month is after the target day, we need to go to next week
    let firstDayOfWeek = firstOfMonth.day(dayOfWeek);

    // If day() returns a day from the previous month, add 7 days
    if (firstDayOfWeek.month() !== date.month()) {
      firstDayOfWeek.add(7, "days");
    }

    // Check if the given date matches the first occurrence
    const isFirst = date.isSame(firstDayOfWeek, "day");

    // Debug for first Sundays
    if (dayOfWeek === 0 && date.date() <= 7) {
      console.log("🔍 isFirstDayOfWeekInMonth check:", {
        date: date.format("YYYY-MM-DD dddd"),
        dayOfWeek,
        firstOfMonth: firstOfMonth.format("YYYY-MM-DD dddd"),
        firstDayOfWeek: firstDayOfWeek.format("YYYY-MM-DD dddd"),
        isFirst,
      });
    }

    return isFirst;
  };

  // Helper function to check if a date is the last occurrence of a day of week in its month
  const isLastDayOfWeekInMonth = (date, dayOfWeek) => {
    if (date.day() !== dayOfWeek) {
      return false;
    }
    const lastOfMonth = date.clone().endOf("month");
    const lastDayOfWeek = lastOfMonth.day(dayOfWeek);
    if (lastDayOfWeek.month() !== date.month()) {
      lastDayOfWeek.subtract(7, "days");
    }
    return date.isSame(lastDayOfWeek, "day");
  };

  // Helper function to check if a date is the Nth occurrence of a day of week in its month
  const isNthDayOfWeekInMonth = (date, dayOfWeek, n) => {
    if (date.day() !== dayOfWeek) {
      return false;
    }
    const firstOfMonth = date.clone().startOf("month");
    let firstOccurrence = firstOfMonth.day(dayOfWeek);
    if (firstOccurrence.month() !== date.month()) {
      firstOccurrence.add(7, "days");
    }
    const nthOccurrence = firstOccurrence.clone().add((n - 1) * 7, "days");
    return date.isSame(nthOccurrence, "day");
  };

  // Helper function to check if a date is closed
  const isDateClosed = useCallback(
    (dateString) => {
      // If no location is selected, don't mark dates as closed
      if (!selectedLocationId && !cartData?.step1Data?.selectedLocation) {
        console.log("⚠️ isDateClosed: No location selected - returning false");
        return false;
      }

      // Get selected location's closedDates and closureRules
      // Try multiple sources to ensure we get the most up-to-date location data
      let selectedLocation =
        cartData?.step1Data?.selectedLocation ||
        locations.find(
          (loc) =>
            loc.id === selectedLocationId || loc._id === selectedLocationId,
        );

      // Always try to get the latest closureRules and closedDates from locations array
      // The locations array has the most up-to-date data from the API
      if (selectedLocation && selectedLocationId) {
        const locationFromArray = locations.find(
          (loc) =>
            loc.id === selectedLocationId || loc._id === selectedLocationId,
        );
        if (locationFromArray) {
          // Merge the location data, prioritizing array data for closureRules/closedDates
          selectedLocation = {
            ...selectedLocation,
            closureRules:
              locationFromArray.closureRules ||
              selectedLocation.closureRules ||
              [],
            closedDates:
              locationFromArray.closedDates ||
              selectedLocation.closedDates ||
              {},
          };
        }
      }

      if (!selectedLocation) {
        console.log(
          "⚠️ isDateClosed: No selected location found - returning false",
        );
        return false;
      }

      // Check closure rules first
      const closureRules = selectedLocation.closureRules || [];
      const checkDate = moment(dateString, [
        "YYYY-MM-DD",
        "dddd, MMMM Do YYYY",
      ]);

      // Debug for first Sundays
      if (checkDate.isValid() && checkDate.day() === 0) {
        const dayOfMonth = checkDate.date();
        if (dayOfMonth <= 7) {
          console.log("🔍 Checking first Sunday:", {
            dateString,
            dayOfMonth,
            location: selectedLocation.name || selectedLocation.seoName,
            closureRulesCount: closureRules.length,
            closureRules: closureRules,
            hasClosedDates: !!selectedLocation.closedDates,
            closedDatesKeys: Object.keys(selectedLocation.closedDates || {}),
          });
        }
      }

      if (checkDate.isValid()) {
        for (const rule of closureRules) {
          const matches = matchesClosureRule(checkDate, rule);
          if (matches) {
            console.log("✅ Date matches closure rule:", {
              dateString,
              ruleType: rule.ruleType,
              ruleName: rule.name,
            });
            return true;
          }
        }
      }

      // Check traditional closedDates
      const closedDates = selectedLocation.closedDates;
      if (
        closedDates &&
        typeof closedDates === "object" &&
        Object.keys(closedDates).length > 0
      ) {
        // Normalize the input date to YYYY-MM-DD format for comparison
        const normalizedInputDate = moment(dateString, [
          "YYYY-MM-DD",
          "dddd, MMMM Do YYYY",
        ]).format("YYYY-MM-DD");

        // Debug for specific dates
        if (normalizedInputDate === "2025-12-07") {
          console.log("🔍 Checking closedDates for 2025-12-07:", {
            normalizedInputDate,
            closedDatesKeys: Object.keys(closedDates),
            hasExactMatch: normalizedInputDate in closedDates,
            exactMatchValue: closedDates[normalizedInputDate],
            closedDatesObject: closedDates,
          });
        }

        // Check exact match first
        if (
          closedDates[normalizedInputDate] === true ||
          closedDates[normalizedInputDate] === "true" ||
          closedDates[normalizedInputDate] === "closed" ||
          closedDates[normalizedInputDate] === 1 ||
          (typeof closedDates[normalizedInputDate] !== "undefined" &&
            closedDates[normalizedInputDate] !== null &&
            closedDates[normalizedInputDate] !== false)
        ) {
          console.log(
            "✅ Date found in closedDates (exact match):",
            normalizedInputDate,
          );
          return true;
        }

        // Check for alternative date formats (without leading zeros)
        const dateWithoutLeadingZero = moment(dateString, [
          "YYYY-MM-DD",
          "dddd, MMMM Do YYYY",
        ]).format("YYYY-M-D");
        if (
          closedDates[dateWithoutLeadingZero] === true ||
          closedDates[dateWithoutLeadingZero] === "true" ||
          closedDates[dateWithoutLeadingZero] === "closed" ||
          closedDates[dateWithoutLeadingZero] === 1 ||
          (typeof closedDates[dateWithoutLeadingZero] !== "undefined" &&
            closedDates[dateWithoutLeadingZero] !== null &&
            closedDates[dateWithoutLeadingZero] !== false)
        ) {
          console.log(
            "✅ Date found in closedDates (no leading zero):",
            dateWithoutLeadingZero,
          );
          return true;
        }

        // Check all keys in closedDates and normalize for comparison
        const closedDateKeys = Object.keys(closedDates);
        for (const key of closedDateKeys) {
          // Normalize both the key and the input date for comparison
          const normalizedKey = moment(key, [
            "YYYY-MM-DD",
            "YYYY-M-D",
            "MM/DD/YYYY",
            "M/D/YYYY",
            "YYYY-M-DD",
            "YYYY-MM-D",
          ]).format("YYYY-MM-DD");

          if (normalizedKey === normalizedInputDate) {
            const keyValue = closedDates[key];
            if (
              keyValue === true ||
              keyValue === "true" ||
              keyValue === "closed" ||
              keyValue === 1 ||
              (typeof keyValue !== "undefined" &&
                keyValue !== null &&
                keyValue !== false)
            ) {
              console.log("✅ Date found in closedDates (normalized match):", {
                originalKey: key,
                normalizedKey,
                normalizedInputDate,
              });
              return true;
            }
          }
        }
      }

      return false;
    },
    [cartData, locations, selectedLocationId, matchesClosureRule],
  );

  const handleDateChange = (dateValue) => {
    console.log("Date button clicked:", dateValue);

    // Clear date error when date is selected
    setDateError("");

    // Note: We'll check offer expiry after we format the date below

    if (dateValue === "Pick a date") {
      console.log("Opening calendar modal");
      handleCalendarModalOpen();
      return;
    }

    let formattedDate;
    let dateStringForCheck;

    if (dateValue === "Today") {
      formattedDate = today.current;
      dateStringForCheck = moment().format("YYYY-MM-DD");
    } else if (dateValue === "Tomorrow") {
      formattedDate = tomorrow.current;
      dateStringForCheck = moment().add(1, "day").format("YYYY-MM-DD");
    } else if (
      (dateValue.includes(" ") && dateValue.includes("st")) ||
      dateValue.includes("nd") ||
      dateValue.includes("rd") ||
      dateValue.includes("th")
    ) {
      const currentYear = new Date().getFullYear();
      formattedDate = moment(
        `${dateValue} ${currentYear}`,
        "dddd Do MMMM YYYY",
      ).format("dddd, MMMM Do YYYY");
      dateStringForCheck = moment(
        `${dateValue} ${currentYear}`,
        "dddd Do MMMM YYYY",
      ).format("YYYY-MM-DD");
    } else {
      formattedDate = dateValue;
      dateStringForCheck = moment(dateValue, "dddd, MMMM Do YYYY").format(
        "YYYY-MM-DD",
      );
    }

    // Check if date is closed
    if (isDateClosed(dateStringForCheck) || isDateClosed(formattedDate)) {
      alert("Sorry, no booking times are available for this date.");
      return;
    }

    // Set the date first, regardless of offer expiry check
    console.log("Setting selected date to:", formattedDate);
    setSelectedDate(formattedDate);
    setSelectedTimeSlot(null); // Reset time slot when date changes

    // Check if there's an offer and if the selected date is after offer expiry
    const offer = cartData?.offerDetails;
    if (offer) {
      const expiryCheck = checkOfferExpiry(offer, formattedDate);
      if (expiryCheck && !expiryCheck.isValid) {
        // Show warning modal (date is already selected above)
        showModal(
          "Offer Expiry Notice",
          `The offer code "${offer.code}" expires on ${expiryCheck.offerEndDate}. Your selected booking date (${expiryCheck.bookingDate}) is after the offer expiry date, so the offer will not apply to this booking.`,
          "warning",
        );
      }
    }

    // Update cart immediately
    setTimeout(() => updateCartStep1(), 100);
  };

  const handleTimeSlotSelect = (slot) => {
    // Validate 1 hour buffer for concierge bookings (ONLY for today - future dates have no buffer requirement)
    const isToday = selectedDate === "Today" || selectedDate === today.current;
    if (isToday) {
      // Only apply 1-hour buffer when booking for today
      const now = moment();
      const minimumBookingTime = now.clone().add(1, "hour");

      // Parse the selected time slot
      const [startTime] = slot.timeRange.split(" - ");
      const slotMoment = moment(startTime, "h:mm A");

      // Set the date to today
      slotMoment.year(now.year());
      slotMoment.month(now.month());
      slotMoment.date(now.date());

      // Check if slot is less than 1 hour away
      if (slotMoment.isBefore(minimumBookingTime)) {
        setTimeSlotError(
          "Sorry, bookings must be made at least 1 hour in advance. Please select a time slot that is at least 1 hour from now.",
        );
        alert(
          "Sorry, bookings must be made at least 1 hour in advance. Please select a time slot that is at least 1 hour from now.",
        );
        return;
      }
    }

    setSelectedTimeSlot(slot);
    // Clear time slot error when time is selected
    setTimeSlotError("");
    setActiveTimeTab(null);
    hasAutoOpenedAccordionRef.current = true;
    // Update cart immediately with the new slot (pass it directly to avoid stale closure)
    setTimeout(() => updateCartStep1(null, null, slot), 100);
  };

  const handleTimeTabChange = (tab) => {
    // Simple toggle: if clicking the same tab, close it; otherwise open the new one
    // Selected time slot is preserved regardless of accordion state
    if (activeTimeTab === tab) {
      setActiveTimeTab(null);
    } else {
      setActiveTimeTab(tab);
    }
  };

  const handleCalendarDateSelect = (selectedDateValue) => {
    // Format the date string
    const dateString = moment(selectedDateValue).format("YYYY-MM-DD");

    // Use isDateClosed function which checks both closedDates and closure rules
    if (isDateClosed(dateString)) {
      alert("Sorry, no booking times are available for this date.");
      return;
    }

    // Format the selected date as a readable string
    const dateStr = moment(selectedDateValue).format("dddd, MMMM Do YYYY");

    console.log("Calendar date selected, formatting to:", dateStr);

    // Close calendar modal first
    setIsCalendarModalOpen(false);

    // Set the date immediately - this will trigger a re-render
    // Use direct assignment to ensure state updates synchronously
    setSelectedDate(dateStr);
    console.log("Date state set to:", dateStr);

    // Clear date error when date is selected from calendar
    setDateError("");
    setSelectedTimeSlot(null); // Reset time slot when date changes

    // Update cart immediately
    updateCartStep1();

    // Store the date for offer expiry check after state updates
    // This will be checked in useEffect after the component re-renders
    setPendingOfferExpiryCheck(dateStr);
  };

  const handleCalendarModalClose = () => {
    setIsCalendarModalOpen(false);
    // Reset to current month when closing
    setDisplayedMonth(moment());
  };

  const handleCalendarModalOpen = () => {
    // Ensure we start from current month when opening
    setDisplayedMonth(moment());
    setIsCalendarModalOpen(true);
  };

  const handlePreviousMonth = () => {
    const today = moment();
    const newMonth = displayedMonth.clone().subtract(1, "month");

    // Don't allow going before current month
    if (newMonth.isBefore(today, "month")) {
      return;
    }

    setDisplayedMonth(newMonth);
  };

  const handleNextMonth = () => {
    const newMonth = displayedMonth.clone().add(1, "month");
    setDisplayedMonth(newMonth);
  };

  const handleContinue = async () => {
    let hasError = false;
    let firstErrorElement = null;

    // Validation with error states
    if (!email || !email.includes("@")) {
      setEmailError("Please enter a valid email address");
      if (!firstErrorElement) {
        firstErrorElement = document.getElementById("email");
      }
      hasError = true;
    } else {
      setEmailError("");
    }

    if (!vehicleType) {
      setVehicleTypeError("Please select a vehicle type");
      if (!firstErrorElement) {
        firstErrorElement = document.getElementById("vehicleType");
      }
      hasError = true;
    } else {
      setVehicleTypeError("");
    }

    if (!selectedLocationId) {
      setLocationError("Please select a location");
      hasError = true;
    } else {
      setLocationError("");
    }

    if (!selectedDate) {
      setDateError("Please select a date");
      hasError = true;
    } else {
      setDateError("");
    }

    if (!selectedTimeSlot) {
      setTimeSlotError("Please select a time slot");
      hasError = true;
    } else {
      setTimeSlotError("");
    }

    // If there are errors, show alert and scroll to first error
    if (hasError) {
      showModal(
        "Missing Information",
        "Please complete all required fields to continue.",
        "warning",
      );

      // Scroll to first error element
      if (firstErrorElement) {
        setTimeout(() => {
          firstErrorElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          firstErrorElement.focus();
        }, 100);
      } else if (!selectedLocationId || !selectedDate || !selectedTimeSlot) {
        // Scroll to location, date, or time section if those are missing
        const locationSection = document.querySelector(
          ".form-group label[for='location']",
        );
        if (!selectedLocationId && locationSection) {
          setTimeout(() => {
            locationSection.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }, 100);
        } else if (!selectedDate) {
          const dateSection = document.querySelector(
            ".arrival-time-section .date-buttons",
          );
          if (dateSection) {
            setTimeout(() => {
              dateSection.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }, 100);
          }
        } else if (!selectedTimeSlot) {
          const timeSection = document.querySelector(
            ".time-selection, .arrival-time-section",
          );
          if (timeSection) {
            setTimeout(() => {
              timeSection.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }, 100);
          }
        }
      }
      return;
    }

    try {
      console.log("Step1 handleContinue - updating cart with data:", {
        email,
        vehicleType,
        selectedDate,
        selectedTimeSlot,
        selectedLocationId,
      });

      // Get the selected location object from the cart data
      const selectedLocation = cartData?.step1Data?.selectedLocation;

      // Final update cart with step 1 data
      const response = await api(
        `booking-flow/cart/${cartData._id}/step1`,
        "PUT",
        {
          email,
          vehicleType,
          selectedDate,
          selectedTimeSlot,
          selectedLocationId,
          selectedLocation, // Include the full location object
        },
      );

      console.log("Step1 API response:", response.data);

      if (response.data.success) {
        console.log("Step1 - calling onCartUpdate and onStepComplete");
        onCartUpdate(response.data.cart);
        // Scroll to top of page
        window.scrollTo({ top: 0, behavior: "smooth" });
        onStepComplete(response.data.cart);
      } else {
        console.error("Step1 API call failed:", response.data);
      }
    } catch (error) {
      console.error("Error updating step 1:", error);
      // Clear cookie if it's a 404 error (cart not found)
      if (error.response?.status === 404) {
        clearCartCookie();
      }
      showModal(
        "Error",
        "Error saving your selection. Please try again.",
        "error",
      );
    }
  };

  const renderTimeTabs = () => {
    // Check if selected date is closed (closure rules or closedDates)
    const now = moment();
    const todayFormatted = now.clone().format("dddd, MMMM Do YYYY");
    const tomorrowFormatted = now
      .clone()
      .add(1, "day")
      .format("dddd, MMMM Do YYYY");
    const possibleFormats = [
      "dddd, MMMM Do YYYY",
      "dddd Do MMMM",
      "ddd Do MMM",
      "YYYY-MM-DD",
    ];
    let actualDateForClosedCheck;
    if (selectedDate === "Today" || selectedDate === todayFormatted) {
      actualDateForClosedCheck = now.clone().format("YYYY-MM-DD");
    } else if (
      selectedDate === "Tomorrow" ||
      selectedDate === tomorrowFormatted
    ) {
      actualDateForClosedCheck = now.clone().add(1, "day").format("YYYY-MM-DD");
    } else if (selectedDate) {
      const parsed = moment(selectedDate, possibleFormats, true);
      actualDateForClosedCheck = parsed.isValid()
        ? parsed.format("YYYY-MM-DD")
        : moment(selectedDate).isValid()
          ? moment(selectedDate).format("YYYY-MM-DD")
          : now.clone().format("YYYY-MM-DD");
    }
    const isSelectedDateClosed =
      selectedLocationId &&
      actualDateForClosedCheck &&
      isDateClosed(actualDateForClosedCheck);

    const tabs = [
      { key: "morning", label: "Morning", timeRange: "7am - 12pm" },
      { key: "afternoon", label: "Afternoon", timeRange: "12pm - 5pm" },
      { key: "evening", label: "Evening", timeRange: "5pm - 8pm" },
    ];

    // Filter out evening tab if there are no time slots available
    const filteredTabs = tabs.filter((tab) => {
      if (tab.key === "evening") {
        // Only show evening if there are available time slots
        return timeSlots.evening && timeSlots.evening.length > 0;
      }
      return true; // Always show morning and afternoon
    });

    return (
      <div className="time-tabs">
        {filteredTabs.map((tab) => (
          <div key={tab.key} className="time-tab-section">
            <div
              className={`time-tab ${
                activeTimeTab === tab.key ? "active" : ""
              }`}
              onClick={() => handleTimeTabChange(tab.key)}
            >
              <div className="time-tab-header">
                <span className="time-tab-label">{tab.label}</span>
                <span className="time-tab-range">{tab.timeRange}</span>
              </div>
              <div className="time-tab-indicator">
                {activeTimeTab === tab.key ? "−" : "+"}
              </div>
            </div>

            {activeTimeTab === tab.key && (
              <div className="time-slots">
                {isSelectedDateClosed ? (
                  <div
                    className="no-slots closed-date-message"
                    style={{ opacity: 0.7 }}
                  >
                    This date is closed for bookings. Please select a different
                    date.
                  </div>
                ) : isLoadingTimeSlots ? (
                  <div className="time-slots-loading">
                    <div className="loading-spinner">⏳</div>
                    <span>Loading available times...</span>
                  </div>
                ) : timeSlots &&
                  timeSlots[tab.key] &&
                  timeSlots[tab.key].length > 0 ? (
                  <div className="time-slots-container">
                    {timeSlots[tab.key].map((slot, index) => {
                      const isUnavailable = !slot.available;
                      const unavailableReason = slot.isPastTime
                        ? "This time slot is within the 1-hour booking buffer"
                        : slot.booked >= slot.totalCapacity
                          ? "This time slot is fully booked"
                          : "This time slot is unavailable";
                      return (
                        <div
                          key={index}
                          className={`time-slot ${
                            selectedTimeSlot?.timeKey === slot.timeKey
                              ? "selected"
                              : ""
                          } ${isUnavailable ? "unavailable" : ""}`}
                          onClick={() => {
                            if (!isUnavailable) {
                              handleTimeSlotSelect(slot);
                            }
                          }}
                          style={{
                            cursor: isUnavailable ? "not-allowed" : "pointer",
                            opacity: isUnavailable ? 0.5 : 1,
                            pointerEvents: isUnavailable ? "none" : "auto",
                          }}
                          title={isUnavailable ? unavailableReason : undefined}
                        >
                          <span className="time-range">
                            {formatArrivalTime(slot)}
                          </span>
                          <div className="time-slot-indicator">
                            {selectedTimeSlot?.timeKey === slot.timeKey
                              ? "✓"
                              : ""}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="no-slots">
                    No time slots available for this period
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderCalendarModal = () => {
    if (!isCalendarModalOpen) return null;

    const today = moment();
    const tomorrow = moment().add(1, "day");
    const dayAfterTomorrow = moment().add(2, "day");
    const fourthDay = moment().add(3, "day"); // Minimum selectable date for calendar (4th day onwards)

    // Ensure displayedMonth is valid and set to current month if not set or invalid
    const currentDisplayedMonth =
      displayedMonth && displayedMonth.isValid() ? displayedMonth : moment();
    const currentMonth = currentDisplayedMonth.clone().startOf("month");
    const daysInMonth = currentMonth.daysInMonth();

    // Get the day of the week for the first day of the month
    // moment().day() returns: 0 = Sunday, 1 = Monday, 2 = Tuesday, ..., 6 = Saturday
    // We want Sunday to be the first column (index 0)
    const firstDayOfMonth = currentMonth.clone();
    let startDay = firstDayOfMonth.day(); // 0 = Sunday, 1 = Monday, etc.

    // Ensure we're starting from Sunday (0) as the first column
    // The weekday headers are: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    // So Sunday = column 0, Monday = column 1, etc.
    // If the first day of the month is Sunday (0), no empty cells needed
    // If the first day is Monday (1), add 1 empty cell for Sunday
    // And so on...

    // Check if we can go to previous month (only if current month is after today's month)
    const canGoPrevious = currentMonth.isAfter(today, "month");

    // Get selected location's closedDates
    // Try cart data first, then locations array, then try to find by ID
    let selectedLocation = cartData?.step1Data?.selectedLocation;

    // If not in cart, try to find in locations array
    if (!selectedLocation && selectedLocationId) {
      selectedLocation = locations.find(
        (loc) =>
          loc.id === selectedLocationId || loc._id === selectedLocationId,
      );
    }

    // Handle closedDates - it can be an object with date strings as keys, or null/undefined
    let closedDates = {};
    if (selectedLocation?.closedDates) {
      if (
        typeof selectedLocation.closedDates === "object" &&
        selectedLocation.closedDates !== null
      ) {
        closedDates = selectedLocation.closedDates;
      }
    }

    // If still no closedDates, try to get from locations array by matching ID
    if (Object.keys(closedDates).length === 0 && selectedLocationId) {
      const locationFromArray = locations.find(
        (loc) =>
          (loc.id === selectedLocationId || loc._id === selectedLocationId) &&
          loc.closedDates,
      );
      if (locationFromArray?.closedDates) {
        closedDates = locationFromArray.closedDates;
        console.log(
          "Calendar - Found closedDates from locations array:",
          closedDates,
        );
      }
    }

    // Check for days with no available slots from daySpecificCapacities
    // IMPORTANT: We should NOT disable entire days of the week based on capacity checks
    // The capacity check only samples a few dates - a day might have no slots one week
    // but have slots another week. Only disable specific dates, not entire days of the week.
    //
    // REMOVED: daysWithNoSlots logic - this was too aggressive and disabled all dates
    // Instead, we'll check individual dates when they're selected, not pre-disable days
    const daysWithNoSlots = new Set();

    // Don't use capacity data to disable entire days of the week
    // This was causing all dates to be disabled when the sampled week had no slots
    // Individual dates will be checked when time slots are loaded
    console.log(
      "📅 Calendar: Not using capacity data to disable days of week (too aggressive)",
    );

    // Debug log to verify closedDates structure (can be removed in production)
    console.log("📅 Calendar Render Summary:", {
      hasLocation: !!selectedLocation,
      locationName:
        selectedLocation?.name || selectedLocation?.seoName || "No location",
      locationId: selectedLocationId,
      closedDatesCount: Object.keys(closedDates).length,
      closedDatesKeys: Object.keys(closedDates),
      closureRulesCount: selectedLocation?.closureRules?.length || 0,
      daysWithNoSlots: Array.from(daysWithNoSlots),
      hasCapacityData: !!locationCapacity,
      capacityDaysChecked: locationCapacity?.daySpecificCapacities
        ? Object.keys(locationCapacity.daySpecificCapacities).length
        : 0,
      fourthDay: fourthDay.format("YYYY-MM-DD"),
      currentMonth: currentMonth.format("MMMM YYYY"),
    });

    if (Object.keys(closedDates).length > 0) {
      console.log(
        "Calendar - Closed dates found for location:",
        selectedLocation?.name || selectedLocation?.seoName,
        closedDates,
      );
      console.log("Calendar - Closed dates keys:", Object.keys(closedDates));
    } else {
      console.log(
        "Calendar - No closed dates found for location:",
        selectedLocation?.name || selectedLocation?.seoName,
      );
      console.log("Calendar - Selected location:", selectedLocation);
    }
    if (daysWithNoSlots.size > 0) {
      console.log(
        "Calendar - Days with no slots:",
        Array.from(daysWithNoSlots),
      );
    }

    // Generate calendar days
    const calendarDays = [];

    // Add empty cells for days before the first day of the month
    // This ensures the first day of the month appears in the correct column
    // Sunday = column 0, Monday = column 1, etc.
    for (let i = 0; i < startDay; i++) {
      calendarDays.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = currentMonth.clone().date(day);
      const dateString = date.format("YYYY-MM-DD");
      const isToday = date.isSame(today, "day");
      const isTomorrow = date.isSame(tomorrow, "day");
      const isThirdDay = date.isSame(dayAfterTomorrow, "day");
      const isPast = date.isBefore(fourthDay, "day"); // Disable today, tomorrow, third day, and past days

      // Check if date is closed - use the isDateClosed function which checks both closedDates and closure rules
      // This function handles all date formats and closure rule evaluation
      // Only check if location is selected - if no location, don't disable dates
      const isClosed = selectedLocation ? isDateClosed(dateString) : false;

      // Debug log for first Sunday of month (e.g., Dec 7, 2025 or Jan 4, 2026)
      if (
        dateString === "2025-12-07" ||
        dateString === "2025-12-7" ||
        dateString === "2026-01-04" ||
        dateString === "2026-1-4"
      ) {
        console.log("🔍 Checking closed date for first Sunday:", {
          dateString,
          isClosed,
          selectedLocation: selectedLocation?.name || selectedLocation?.seoName,
          locationId: selectedLocation?.id || selectedLocation?._id,
          closureRules: selectedLocation?.closureRules || [],
          closureRulesCount: (selectedLocation?.closureRules || []).length,
          closedDates: selectedLocation?.closedDates || {},
          closedDatesKeys: Object.keys(selectedLocation?.closedDates || {}),
          dateDayOfWeek: date.format("dddd"),
          dateDayOfMonth: date.date(),
        });
      }

      // Debug for third Sunday (e.g., March 15, 2026 - Stafford closure)
      if (dateString === "2026-03-15") {
        console.log("🔍 Checking third Sunday (March 15, 2026):", {
          dateString,
          isClosed,
          selectedLocation: selectedLocation?.name || selectedLocation?.seoName,
          closureRulesCount: (selectedLocation?.closureRules || []).length,
          closureRules: selectedLocation?.closureRules || [],
          ruleTypes: (selectedLocation?.closureRules || []).map(
            (r) => r.ruleType,
          ),
        });
      }

      // Check if this day of the week has no available slots
      const dayOfWeek = date.format("dddd").toLowerCase(); // e.g., "sunday", "monday"
      const hasNoSlotsForDay = daysWithNoSlots.has(dayOfWeek);

      // Debug logging for first few dates to understand why they're disabled
      if (day <= 10) {
        console.log(`📅 Date ${dateString} (${dayOfWeek}):`, {
          isPast,
          isClosed,
          hasNoSlotsForDay,
          isDisabled: isPast || isClosed || hasNoSlotsForDay,
          selectedLocation: selectedLocation?.name || "No location selected",
        });
      }

      const publicHolidayName = publicHolidayByDate[dateString] || null;

      calendarDays.push({
        day,
        date: dateString,
        isToday,
        isTomorrow,
        isThirdDay,
        isPast,
        isClosed, // Add closed date flag
        hasNoSlotsForDay, // Add flag for days with no available slots
        publicHolidayName,
      });
    }

    return (
      <div
        className="calendar-modal-overlay"
        onClick={handleCalendarModalClose}
      >
        <div className="calendar-modal" onClick={(e) => e.stopPropagation()}>
          <div className="calendar-modal-header">
            <h3>Select Date</h3>
            <button
              className="calendar-modal-close"
              onClick={handleCalendarModalClose}
            >
              ×
            </button>
          </div>

          <div className="calendar-modal-body">
            <div className="calendar-month-header">
              <button
                className="calendar-nav-button"
                onClick={handlePreviousMonth}
                disabled={!canGoPrevious}
                title="Previous month"
              >
                ‹
              </button>
              <h4>{currentMonth.format("MMMM YYYY")}</h4>
              <button
                className="calendar-nav-button"
                onClick={handleNextMonth}
                title="Next month"
              >
                ›
              </button>
            </div>

            <div className="calendar-weekdays">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div key={day} className="calendar-weekday">
                  {day}
                </div>
              ))}
            </div>

            <div className="calendar-days">
              {calendarDays.map((dayData, index) => {
                const isDisabled =
                  dayData?.isPast ||
                  dayData?.isClosed ||
                  dayData?.hasNoSlotsForDay;
                return (
                  <div
                    key={index}
                    className={`calendar-day ${
                      dayData ? (dayData.isToday ? "today" : "") : "empty"
                    } ${isDisabled ? "past" : ""} ${
                      dayData?.isClosed ? "closed" : ""
                    } ${dayData?.hasNoSlotsForDay ? "no-slots" : ""} ${
                      dayData?.publicHolidayName &&
                      !dayData?.isPast &&
                      !dayData?.isClosed &&
                      !dayData?.hasNoSlotsForDay
                        ? "public-holiday"
                        : ""
                    }`}
                    onClick={(e) => {
                      // Completely prevent selection if disabled
                      if (isDisabled) {
                        e.preventDefault();
                        e.stopPropagation();
                        const message = dayData?.isClosed
                          ? "Sorry, this date is closed for bookings."
                          : dayData?.hasNoSlotsForDay
                            ? "Sorry, no booking times are available for this day."
                            : "This date is not available.";
                        alert(message);
                        return;
                      }

                      // Only allow selection if not disabled
                      if (dayData) {
                        handleCalendarDateSelect(dayData.date);
                      }
                    }}
                    style={{
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      // Don't set opacity inline for closed dates - let CSS handle it
                      opacity: dayData?.isClosed
                        ? undefined
                        : isDisabled
                          ? 0.5
                          : 1,
                      pointerEvents: isDisabled ? "none" : "auto",
                    }}
                    title={
                      dayData?.isClosed
                        ? "This date is closed for bookings"
                        : dayData?.hasNoSlotsForDay
                          ? "No booking times available for this day"
                          : dayData?.isPast
                            ? "This date is not available"
                            : dayData?.publicHolidayName
                              ? `${dayData.publicHolidayName}. ${PUBLIC_HOLIDAY_SURCHARGE_NOTICE}`
                              : ""
                    }
                  >
                    {dayData ? (
                      <>
                        <span className="calendar-day-number">
                          {dayData.day}
                        </span>
                        {dayData.publicHolidayName ? (
                          <span className="calendar-ph-label">PH</span>
                        ) : null}
                      </>
                    ) : (
                      ""
                    )}
                  </div>
                );
              })}
            </div>
            <p className="calendar-ph-legend">
              <strong>PH</strong> {PUBLIC_HOLIDAY_CALENDAR_LEGEND}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // All useEffect hooks moved here to avoid hoisting issues
  // Create cart, load locations, and get user location on component mount
  useEffect(() => {
    const initialize = async () => {
      await createInitialCart();
      const loadedLocations = await loadAllLocations();

      // Only try to get user location if we don't have a saved location in cart
      if (!cartData?.step1Data?.selectedLocationId) {
        // Get user location and calculate closest locations
        const userCoords = await getUserLocation();
        if (userCoords && loadedLocations.length > 0) {
          const closest = calculateClosestLocations(
            userCoords,
            loadedLocations,
          );
          setClosestLocations(closest);
          setShowLocationInput(false); // Hide input initially, show closest locations
        } else {
          // If we can't get user location, show the input
          setShowLocationInput(true);
        }
      } else {
        // If we have a saved location, show the input (user can search for others)
        setShowLocationInput(true);
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  useEffect(() => {
    let cancelled = false;

    const locationForPh =
      cartData?.step1Data?.selectedLocation ||
      (selectedLocationId
        ? locations.find(
            (loc) =>
              loc.id === selectedLocationId || loc._id === selectedLocationId,
          )
        : null);

    const phQuery = publicHolidaysQueryForLocation(locationForPh);
    if (!phQuery) {
      setPublicHolidayByDate({});
      return undefined;
    }

    (async () => {
      try {
        const res = await api(`booking-flow/public-holidays?${phQuery}`, "GET");
        if (
          !cancelled &&
          res.data?.success &&
          res.data?.holidaysByDate &&
          typeof res.data.holidaysByDate === "object"
        ) {
          setPublicHolidayByDate(res.data.holidaysByDate);
        }
      } catch (e) {
        console.warn("Could not load public holidays:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    cartData?.step1Data?.selectedLocation,
    selectedLocationId,
    locations,
  ]);

  // Check URL params for codes whenever cart data changes
  useEffect(() => {
    if (cartData?._id) {
      checkUrlParamsForCodes(cartData._id);
    }
  }, [cartData?._id, checkUrlParamsForCodes]);

  // Initialize from cart data if available (only on initial load)
  useEffect(() => {
    if (cartData?.step1Data && !hasInitializedFromCartRef.current) {
      // Only initialize from cart data once on initial load
      console.log("Initializing from cart data:", cartData.step1Data);
      const {
        email: cartEmail,
        vehicleType: cartVehicleType,
        selectedDate: cartDate,
        selectedTimeSlot: cartTimeSlot,
        selectedLocationId: cartLocationId,
      } = cartData.step1Data;

      // Set email and vehicle type
      if (cartEmail) setEmail(cartEmail);
      if (cartVehicleType) setVehicleType(cartVehicleType);

      // Only set date from cart if there's actually a saved date, otherwise keep "Today"
      console.log("Cart date from cart data:", cartDate);
      const currentYear = new Date().getFullYear();

      // Check if cart date is a valid option (not "Pick a time" or other invalid values)
      const validDateOptions = [
        "Today",
        "Tomorrow",
        "Friday 24 June",
        "Pick a date",
      ];
      const isCustomDate =
        cartDate &&
        cartDate.includes(",") &&
        /(st|nd|rd|th)/.test(cartDate) &&
        cartDate.includes(currentYear.toString()); // Custom calendar date format
      const hasValidCartDate =
        cartDate &&
        cartDate.trim() !== "" &&
        cartDate !== "Today" &&
        !cartDate.includes("Pick a time") &&
        (validDateOptions.includes(cartDate) || isCustomDate);

      if (hasValidCartDate) {
        console.log("Setting date from cart:", cartDate);
        setSelectedDate(cartDate);
      } else {
        console.log("Keeping default date: Today (cart date invalid or empty)");
        setSelectedDate(today.current);
      }

      // Set location first
      if (cartLocationId) {
        console.log("Setting location ID from cart:", cartLocationId);
        setSelectedLocationId(cartLocationId);
      }

      // Store time slot to set after location is set (prevents it from being cleared)
      if (cartTimeSlot && cartTimeSlot.timeKey) {
        cartTimeSlotRef.current = cartTimeSlot;

        // Auto-open accordion on initial page load if a time slot is selected
        const hour = parseInt(cartTimeSlot.timeKey.split(":")[0]);
        if (hour < 12) {
          setActiveTimeTab("morning");
        } else if (hour < 17) {
          setActiveTimeTab("afternoon");
        } else {
          // Only open evening if there are time slots available
          if (timeSlots.evening && timeSlots.evening.length > 0) {
            setActiveTimeTab("evening");
          } else {
            // If evening is not available, default to afternoon
            setActiveTimeTab("afternoon");
          }
        }
        hasAutoOpenedAccordionRef.current = true; // Mark as auto-opened so it doesn't reopen
      }

      // Mark as initialized so we don't run this again
      hasInitializedFromCartRef.current = true;
    }
  }, [cartData]);

  // Set time slot from cart after location is set (to prevent it from being cleared)
  useEffect(() => {
    if (cartTimeSlotRef.current && selectedLocationId && !selectedTimeSlot) {
      console.log(
        "Setting time slot from cart ref after location is set:",
        cartTimeSlotRef.current,
      );
      const cartTimeSlot = cartTimeSlotRef.current;
      setSelectedTimeSlot(cartTimeSlot);
      // Update cart with the time slot from cart
      setTimeout(() => updateCartStep1(null, null, cartTimeSlot), 100);
      cartTimeSlotRef.current = null; // Clear ref after setting
    }
  }, [selectedLocationId, selectedTimeSlot, updateCartStep1]);

  // Set location search text when locations are loaded and we have a saved location ID
  useEffect(() => {
    if (selectedLocationId && locations.length > 0 && !locationSearch) {
      console.log("Looking for location with ID:", selectedLocationId);
      console.log("Available locations:", locations.length);
      const savedLocation = locations.find(
        (loc) => loc.id === selectedLocationId,
      );
      if (savedLocation) {
        console.log(
          "Found saved location:",
          savedLocation.seoName || savedLocation.name,
        );
        setLocationSearch(savedLocation.seoName || savedLocation.name);
      } else {
        console.log("Saved location not found in locations list");
      }
    }
  }, [selectedLocationId, locations, locationSearch]);

  // Fetch location capacity when location changes (for calendar day disabling)
  // Fetch location capacity to determine which days have no available slots
  const fetchLocationCapacity = useCallback(async (locationId) => {
    if (!locationId) {
      setLocationCapacity(null);
      return;
    }

    try {
      // Get a reference date that's at least 4 days in the future (to avoid past dates)
      const today = moment();
      const fourthDay = moment().add(3, "day");
      const nextSunday = moment().day(7); // Next Sunday

      // Use the later of next Sunday or the 4th day to ensure we're checking valid future dates
      const startDate = nextSunday.isAfter(fourthDay) ? nextSunday : fourthDay;
      const daysToCheck = [];

      // Create dates for each day of the week starting from the start date
      for (let i = 0; i < 7; i++) {
        const checkDate = startDate.clone().add(i, "days");
        // Only check dates that are at least 4 days in the future
        if (
          checkDate.isAfter(fourthDay) ||
          checkDate.isSame(fourthDay, "day")
        ) {
          daysToCheck.push(checkDate);
        }
      }

      // Check timeslots for each day to determine which days have no slots
      const daySpecificCapacities = {};
      const checkPromises = daysToCheck.map(async (date) => {
        const dayName = date.format("dddd").toLowerCase();
        const dateStr = date.format("YYYY-MM-DD");

        try {
          const response = await api(
            `booking-flow/timeslots/${locationId}?date=${dateStr}`,
            "GET",
          );

          if (response && response.data && response.data.success) {
            // Check if the date is closed (isClosed flag)
            if (response.data.isClosed) {
              // Don't mark as no slots - closed dates are handled separately
              console.log(
                `Date ${dateStr} (${dayName}) is closed - skipping capacity check`,
              );
              return;
            }

            const timeSlots = response.data.timeSlots;
            const totalSlots =
              (timeSlots?.morning?.length || 0) +
              (timeSlots?.afternoon?.length || 0) +
              (timeSlots?.evening?.length || 0);

            // Store capacity info but don't use it to disable entire days of the week
            // This is just for informational purposes - individual dates will be checked
            // when time slots are loaded for that specific date
            if (totalSlots === 0 && !response.data.isClosed) {
              // Don't mark entire day as having no slots - just log it
              // The day might have slots on other dates
              console.log(
                `ℹ️ No slots available for ${dayName} on ${dateStr} (but other ${dayName}s may have slots)`,
              );
              // Don't add to daySpecificCapacities - we don't want to disable all days of this type
            } else {
              console.log(
                `✅ Slots available for ${dayName} (${dateStr}): ${totalSlots} total`,
              );
            }
          } else {
            console.warn(
              `⚠️ Invalid response for ${dayName} (${dateStr}):`,
              response?.data,
            );
          }
        } catch (error) {
          // Don't mark as no slots on error - just skip this day
          // This prevents API failures from disabling all dates
          console.error(
            `❌ Error checking capacity for ${dayName} (${dateStr}):`,
            error.message || error,
          );
          // Don't add to daySpecificCapacities on error - let the day remain available
        }
      });

      await Promise.all(checkPromises);

      // Don't store capacity data that disables entire days of the week
      // The capacity check is just for informational purposes
      // Individual dates will be checked when time slots are loaded
      console.log(
        `✅ Location capacity check completed (informational only - not used to disable days)`,
      );

      // Set to null so we don't use this data to disable dates
      // Individual date availability will be checked when time slots are loaded
      setLocationCapacity(null);
    } catch (error) {
      console.error("Error fetching location capacity:", error);
      // Set to null on error to prevent disabling all dates
      setLocationCapacity(null);
    }
  }, []);

  // Fetch capacity when location is selected
  useEffect(() => {
    if (selectedLocationId) {
      fetchLocationCapacity(selectedLocationId);
    } else {
      setLocationCapacity(null);
    }
  }, [selectedLocationId, fetchLocationCapacity]);

  // Load time slots when location or date changes
  useEffect(() => {
    if (!selectedLocationId || !selectedDate) return;

    // Convert selectedDate to YYYY-MM-DD for closure check
    const now = moment();
    const todayFormatted = now.clone().format("dddd, MMMM Do YYYY");
    const tomorrowFormatted = now
      .clone()
      .add(1, "day")
      .format("dddd, MMMM Do YYYY");
    const possibleFormats = [
      "dddd, MMMM Do YYYY",
      "dddd Do MMMM",
      "ddd Do MMM",
      "YYYY-MM-DD",
    ];

    let actualDate;
    if (selectedDate === "Today" || selectedDate === todayFormatted) {
      actualDate = now.clone().format("YYYY-MM-DD");
    } else if (
      selectedDate === "Tomorrow" ||
      selectedDate === tomorrowFormatted
    ) {
      actualDate = now.clone().add(1, "day").format("YYYY-MM-DD");
    } else {
      const parsed = moment(selectedDate, possibleFormats, true);
      actualDate = parsed.isValid()
        ? parsed.format("YYYY-MM-DD")
        : moment(selectedDate).isValid()
          ? moment(selectedDate).format("YYYY-MM-DD")
          : now.clone().format("YYYY-MM-DD");
    }

    // Don't fetch time slots for closed dates - show empty/greyed out like calendar
    if (isDateClosed(actualDate)) {
      setTimeSlots({ morning: [], afternoon: [], evening: [] });
      setSelectedTimeSlot(null); // Clear any previously selected time
      return;
    }

    loadTimeSlots(selectedLocationId);
  }, [selectedLocationId, selectedDate, loadTimeSlots, isDateClosed]);

  // Check offer expiry after date is selected and component has re-rendered
  useEffect(() => {
    if (pendingOfferExpiryCheck && cartData?.offerDetails) {
      const offer = cartData.offerDetails;
      const expiryCheck = checkOfferExpiry(offer, pendingOfferExpiryCheck);
      if (expiryCheck && !expiryCheck.isValid) {
        // Show warning modal (date is already selected and UI is updated)
        showModal(
          "Offer Expiry Notice",
          `The offer code "${offer.code}" expires on ${expiryCheck.offerEndDate}. Your selected booking date (${expiryCheck.bookingDate}) is after the offer expiry date, so the offer will not apply to this booking.`,
          "warning",
        );
      }
      // Clear the pending check
      setPendingOfferExpiryCheck(null);
    }
  }, [
    pendingOfferExpiryCheck,
    cartData?.offerDetails,
    checkOfferExpiry,
    showModal,
  ]);

  // Auto-open accordion on page load if time slots are loaded and we have a selected time slot
  // Only do this once (tracked by ref), then let user control it
  useEffect(() => {
    if (
      selectedTimeSlot &&
      timeSlots &&
      !activeTimeTab &&
      !hasAutoOpenedAccordionRef.current
    ) {
      const hour = parseInt(selectedTimeSlot.timeKey?.split(":")[0] || 0);
      if (hour < 12) {
        setActiveTimeTab("morning");
      } else if (hour < 17) {
        setActiveTimeTab("afternoon");
      } else {
        // Only open evening if there are time slots available
        if (timeSlots.evening && timeSlots.evening.length > 0) {
          setActiveTimeTab("evening");
        } else {
          // If evening is not available, default to afternoon
          setActiveTimeTab("afternoon");
        }
      }
      hasAutoOpenedAccordionRef.current = true; // Mark as auto-opened so it doesn't reopen
    }
  }, [selectedTimeSlot, timeSlots, activeTimeTab]);

  // Search locations when debounced search value changes
  useEffect(() => {
    if (locations.length > 0) {
      searchLocations(debouncedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, locations]);

  const selectedDateIsoForPh = selectedBookingDateToIso(selectedDate);
  const selectedPublicHolidayName =
    selectedDateIsoForPh && publicHolidayByDate[selectedDateIsoForPh]
      ? publicHolidayByDate[selectedDateIsoForPh]
      : null;

  return (
    <div className="step1-container">
      {/* Main Content - Centered */}
      <div className="step1-content">
        {/* Logo */}
        <div className="step1-header">
          <div className="logo">
            <img
              src={ConciergeLogo}
              alt="Concierge Car Wash"
              className="logo-image"
            />
          </div>
        </div>

        {/* Form Fields */}
        <div className="form-fields">
          <div className={`form-group ${emailError ? "has-error" : ""}`}>
            <label htmlFor="email">Email</label>
            <div className="input-container">
              <input
                type="email"
                id="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="Enter your email"
                className={emailError ? "error" : ""}
                disabled={isLoading}
              />
            </div>
            {emailError && (
              <div className="booking-flow-error-message">{emailError}</div>
            )}
          </div>

          <div className={`form-group ${vehicleTypeError ? "has-error" : ""}`}>
            <label htmlFor="vehicleType">Choose a Vehicle type</label>
            <select
              id="vehicleType"
              value={vehicleType}
              onChange={handleVehicleTypeChange}
              disabled={isLoading}
              className={vehicleTypeError ? "error" : ""}
            >
              <option value="">Select vehicle type</option>
              {vehicleTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            {vehicleTypeError && (
              <div className="booking-flow-error-message">
                {vehicleTypeError}
              </div>
            )}
          </div>

          <div className={`form-group ${locationError ? "has-error" : ""}`}>
            <label htmlFor="location">Choose a Location</label>
            {locationError && (
              <div className="booking-flow-error-message">{locationError}</div>
            )}

            {/* Show closest locations if available and input is hidden */}
            {!showLocationInput &&
              closestLocations.length > 0 &&
              (() => {
                // Check if selected location is in the closest locations list
                const selectedLocationInList = closestLocations.find(
                  (loc) => loc.id === selectedLocationId,
                );

                // If a location is selected but not in closest locations, find and add it
                let locationsToDisplay = [...closestLocations];
                if (selectedLocationId && !selectedLocationInList) {
                  const selectedLocation = locations.find(
                    (loc) => loc.id === selectedLocationId,
                  );
                  if (selectedLocation) {
                    // Calculate distance for selected location if user location is available
                    let locationWithDistance = { ...selectedLocation };
                    if (userLocation) {
                      const lat =
                        selectedLocation.coordinates?.lat ||
                        selectedLocation.lat;
                      const lng =
                        selectedLocation.coordinates?.lng ||
                        selectedLocation.lng;
                      if (lat && lng) {
                        const distance = calculateDistance(
                          userLocation.lat,
                          userLocation.lng,
                          lat,
                          lng,
                        );
                        locationWithDistance.distance =
                          Math.round(distance * 10) / 10;
                      }
                    }
                    // Add selected location at the beginning
                    locationsToDisplay = [
                      locationWithDistance,
                      ...closestLocations,
                    ];
                  }
                }

                return (
                  <div className="closest-locations-container">
                    {isLoadingUserLocation ? (
                      <div className="location-loading">
                        <div className="loading-spinner-small">
                          <div className="spinner-small"></div>
                        </div>
                        <span>Finding locations near you...</span>
                      </div>
                    ) : (
                      <>
                        {locationsToDisplay.map((location, index) => (
                          <div
                            key={location.id}
                            className={`location-option closest-location ${
                              selectedLocationId === location.id
                                ? "selected"
                                : ""
                            }`}
                            onClick={() => handleLocationSelect(location)}
                          >
                            <div className="location-name">
                              {location.seoName || location.name}
                            </div>
                            <div className="location-address">
                              {location.address}
                            </div>
                            {location.city && (
                              <div className="location-city">
                                {location.city}
                              </div>
                            )}
                            {location.distance && (
                              <div className="location-distance">
                                {location.distance}km away
                              </div>
                            )}
                          </div>
                        ))}
                        <div
                          className="location-option choose-another-location"
                          onClick={() => {
                            // Clear selected location
                            setSelectedLocationId(null);
                            // Show input
                            setShowLocationInput(true);
                            setIsLocationInputFocused(true);
                            // Clear search value and filtered locations
                            setLocationSearch("");
                            setFilteredLocations([]);
                            // Clear selected time slot since location changed
                            setSelectedTimeSlot(null);
                            setActiveTimeTab(null);
                            // Focus the input after state updates
                            setTimeout(() => {
                              const input = document.getElementById("location");
                              if (input) {
                                input.focus();
                                input.value = ""; // Ensure input value is cleared
                              }
                            }, 100);
                          }}
                        >
                          <div className="location-name">
                            Choose another location
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}

            {/* Show input when showLocationInput is true or no closest locations */}
            {(showLocationInput || closestLocations.length === 0) && (
              <div className="location-search-container">
                <input
                  type="text"
                  id="location"
                  placeholder="Enter name, postcode or suburb to select location"
                  value={locationSearch}
                  onChange={handleLocationSearchChange}
                  onFocus={handleLocationInputFocus}
                  onBlur={handleLocationInputBlur}
                  disabled={isLoading}
                  className="location-search-input"
                />
                {isSearching && locationSearch.length >= 2 && (
                  <div className="location-search-loading">
                    <div className="loading-spinner-small">
                      <div className="spinner-small"></div>
                    </div>
                    <span>Searching locations...</span>
                  </div>
                )}
                {isLocationInputFocused && filteredLocations.length > 0 && (
                  <div className="location-dropdown">
                    {filteredLocations.map((location) => (
                      <div
                        key={location.id}
                        className="location-option"
                        onClick={() => handleLocationSelect(location)}
                      >
                        <div className="location-name">
                          {location.seoName || location.name}
                        </div>
                        <div className="location-address">
                          {location.address}
                        </div>
                        {location.city && (
                          <div className="location-city">{location.city}</div>
                        )}
                        {location.distance && (
                          <div className="location-distance">
                            {location.distance}km away
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {isLocationInputFocused &&
                  locationSearch.length >= 2 &&
                  filteredLocations.length === 0 &&
                  !isSearching &&
                  showNoResults && (
                    <div className="no-locations-found">
                      {/^\d{4}$/.test(locationSearch) ? (
                        <div>
                          <div>
                            No locations found within 50km of your detected
                            location.
                          </div>
                          <div
                            style={{
                              marginTop: "10px",
                              fontSize: "14px",
                              color: "#666",
                            }}
                          >
                            Try searching by location name instead (e.g.,
                            "Doncaster", "Broadway", "Melbourne").
                          </div>
                        </div>
                      ) : (
                        `No locations found for "${locationSearch}"`
                      )}
                    </div>
                  )}
              </div>
            )}
          </div>
        </div>

        {/* Expected Arrival Time Section */}
        <div className={`arrival-time-section ${dateError ? "has-error" : ""}`}>
          <h3>Expected Arrival Time</h3>
          {dateError && (
            <div className="booking-flow-error-message">{dateError}</div>
          )}
          <div className="date-buttons">
            {dateOptions.map((option) => {
              // Check if this is a custom date (from calendar) - format: "Wednesday, December 25th 2024"
              // Accept any year, not just current year, to handle dates from calendar picker
              const isCustomDate =
                selectedDate &&
                selectedDate.includes(",") &&
                /(st|nd|rd|th)/.test(selectedDate) &&
                /\d{4}/.test(selectedDate); // Check for any 4-digit year instead of just current year
              const isPickADate = option.value === "Pick a date";

              // Check if selected date matches the dynamic third day
              const dayAfterTomorrow = moment().add(2, "day");
              const thirdDayFormatted =
                dayAfterTomorrow.format("dddd, MMMM Do YYYY");
              const isThirdDaySelected = selectedDate === thirdDayFormatted;

              // Check if selected date matches Today or Tomorrow
              const todayFormatted = moment().format("dddd, MMMM Do YYYY");
              const tomorrowFormatted = moment()
                .add(1, "day")
                .format("dddd, MMMM Do YYYY");
              const isTodaySelected = selectedDate === todayFormatted;
              const isTomorrowSelected = selectedDate === tomorrowFormatted;

              // Determine if this button should be selected
              let isSelected = false;

              if (option.value === "Today" && isTodaySelected) {
                isSelected = true;
              } else if (option.value === "Tomorrow" && isTomorrowSelected) {
                isSelected = true;
              } else if (
                option.value === dayAfterTomorrow.format("dddd Do MMMM") &&
                isThirdDaySelected
              ) {
                isSelected = true;
              } else if (
                isPickADate &&
                isCustomDate &&
                !isTodaySelected &&
                !isTomorrowSelected &&
                !isThirdDaySelected
              ) {
                // Only show custom date in "Pick a date" if it's not one of the three preset options
                isSelected = true;
              }

              // Determine what text to show
              let buttonText = option.label;
              if (
                isPickADate &&
                isCustomDate &&
                !isTodaySelected &&
                !isTomorrowSelected &&
                !isThirdDaySelected
              ) {
                // Convert long format "Wednesday, October 1st 2025" to abbreviated format "Wed 1st Oct"
                try {
                  const dateObj = moment(selectedDate, "dddd, MMMM Do YYYY");
                  buttonText = dateObj.format("ddd Do MMM"); // e.g., "Wed 1st Oct" - same as 3rd button
                } catch (error) {
                  console.error("Error formatting date:", error);
                  buttonText = selectedDate; // Fallback to original
                }
              }

              // Check if this date option is closed
              let isDateOptionClosed = false;
              let dateStringToCheck = "";

              if (option.dateToCheck) {
                // Use explicit dateToCheck from Today/Tomorrow/3rd-day options
                dateStringToCheck = option.dateToCheck;
              } else if (
                isPickADate &&
                isCustomDate &&
                !isTodaySelected &&
                !isTomorrowSelected &&
                !isThirdDaySelected
              ) {
                // Custom date from calendar - parse and check closure
                const possibleFormats = [
                  "dddd, MMMM Do YYYY",
                  "dddd Do MMMM",
                  "ddd Do MMM",
                  "YYYY-MM-DD",
                ];
                const parsed = moment(selectedDate, possibleFormats, true);
                dateStringToCheck = parsed.isValid()
                  ? parsed.format("YYYY-MM-DD")
                  : moment(selectedDate).isValid()
                    ? moment(selectedDate).format("YYYY-MM-DD")
                    : "";
              }

              if (dateStringToCheck) {
                // Use the isDateClosed helper function
                // Only check if location is selected - if no location, don't disable dates
                isDateOptionClosed = selectedLocationId
                  ? isDateClosed(dateStringToCheck)
                  : false;
              }

              return (
                <button
                  key={option.value}
                  className={`date-button ${isSelected ? "selected" : ""} ${
                    isDateOptionClosed ? "disabled" : ""
                  }`}
                  onClick={() => {
                    if (!isDateOptionClosed) {
                      handleDateChange(option.value);
                    } else {
                      alert(
                        "Sorry, no booking times are available for this date.",
                      );
                    }
                  }}
                  disabled={isLoading || isDateOptionClosed}
                  style={{
                    opacity: isDateOptionClosed ? 0.5 : 1,
                    cursor: isDateOptionClosed ? "not-allowed" : "pointer",
                  }}
                  title={
                    isDateOptionClosed ? "This date is closed for bookings" : ""
                  }
                >
                  {buttonText}
                </button>
              );
            })}
          </div>
          {selectedPublicHolidayName ? (
            <div className="public-holiday-surcharge-notice" role="status">
              <strong>{selectedPublicHolidayName}</strong>
              <span> — {PUBLIC_HOLIDAY_SURCHARGE_NOTICE}</span>
            </div>
          ) : null}

          {/* Time Selection */}
          {selectedDate && selectedLocationId && (
            <div className="time-selection-wrapper">
              {timeSlotError && (
                <div className="booking-flow-error-message">
                  {timeSlotError}
                </div>
              )}
              {selectedTimeSlot ? (
                <div className="selected-time-display">
                  <div className="selected-time-summary">
                    <span className="selected-time-label">
                      Selected date & time
                    </span>
                    <span className="selected-time-value">
                      {(() => {
                        // Format date display
                        let dateDisplay = "";
                        if (selectedDate === "Today") {
                          dateDisplay = "Today";
                        } else if (selectedDate === "Tomorrow") {
                          dateDisplay = "Tomorrow";
                        } else {
                          // Check if it matches today or tomorrow format
                          const todayFormatted =
                            moment().format("dddd, MMMM Do YYYY");
                          const tomorrowFormatted = moment()
                            .add(1, "day")
                            .format("dddd, MMMM Do YYYY");
                          if (selectedDate === todayFormatted) {
                            dateDisplay = "Today";
                          } else if (selectedDate === tomorrowFormatted) {
                            dateDisplay = "Tomorrow";
                          } else {
                            // Format custom date to shorter format
                            try {
                              const dateObj = moment(
                                selectedDate,
                                "dddd, MMMM Do YYYY",
                              );
                              if (dateObj.isValid()) {
                                dateDisplay = dateObj.format("ddd Do MMM"); // e.g., "Wed 1st Oct"
                              } else {
                                dateDisplay = selectedDate;
                              }
                            } catch (error) {
                              dateDisplay = selectedDate;
                            }
                          }
                        }
                        const timeDisplay = formatArrivalTime(selectedTimeSlot);
                        return `${dateDisplay} at ${timeDisplay}`;
                      })()}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="change-time-button"
                    onClick={() => {
                      setSelectedTimeSlot(null);
                      setActiveTimeTab(null);
                      setTimeSlotError("");
                    }}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div
                  className={`time-selection ${
                    timeSlotError ? "has-error" : ""
                  }`}
                >
                  {renderTimeTabs()}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Continue Button */}
        <div className="step1-actions">
          <button
            className="continue-button"
            onClick={handleContinue}
            disabled={isLoading}
          >
            Continue
          </button>
        </div>
      </div>

      {/* Calendar Modal */}
      {renderCalendarModal()}

      {/* Alert Modal */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        showCancel={modal.showCancel}
        onConfirm={modal.onConfirm}
        titleIcon={null}
      />

      {/* Offer Modal */}
      {offerModal.isOpen && offerModal.offer && (
        <div className="special-offer-overlay">
          <div className="special-offer-container">
            <div className="special-offer-header">
              <h3>Your Special Offer is Now Active!</h3>
            </div>
            <div className="special-offer-body">
              <p style={{ marginBottom: "16px" }}>
                To apply this discount at checkout, please ensure:
              </p>
              {offerModal.offer.services &&
                offerModal.offer.services.filter(Boolean).length > 0 && (
                  <div style={{ marginBottom: "16px" }}>
                    <p style={{ fontWeight: "600", marginBottom: "8px" }}>
                      {offerModal.offer.services.filter(Boolean).length > 1
                        ? "One of the services below is required:"
                        : "The following service is required:"}
                    </p>
                    <ul style={{ margin: "0", paddingLeft: "20px" }}>
                      {offerModal.offer.services
                        .filter(Boolean)
                        .map((service, index) => (
                          <li key={index} style={{ marginBottom: "4px" }}>
                            {service.name || service}
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              {offerModal.offer.code !== "MUMDAY25OFF" &&
              offerModal.offer.showSitesRequiredInModal !== false &&
              ((offerModal.offer.locations &&
                offerModal.offer.locations.length > 0) ||
                offerModal.offer.location) ? (
                <div style={{ marginBottom: "16px" }}>
                  <p style={{ fontWeight: "600", marginBottom: "8px" }}>
                    {(offerModal.offer.locations &&
                      offerModal.offer.locations.length > 1) ||
                    (offerModal.offer.locations &&
                      offerModal.offer.locations.length === 1 &&
                      offerModal.offer.location)
                      ? "One of the following locations will need to be selected to apply the offer:"
                      : "The following location will need to be selected to apply the offer:"}
                  </p>
                  <ul style={{ margin: "0", paddingLeft: "20px" }}>
                    {offerModal.offer.locations &&
                    offerModal.offer.locations.length > 0
                      ? offerModal.offer.locations
                          .filter(Boolean)
                          .map((location, index) => (
                            <li key={index} style={{ marginBottom: "4px" }}>
                              {location.name || location.seoName || location}
                            </li>
                          ))
                      : offerModal.offer.location && (
                          <li style={{ marginBottom: "4px" }}>
                            {offerModal.offer.location.name ||
                              offerModal.offer.location.seoName ||
                              offerModal.offer.location}
                          </li>
                        )}
                  </ul>
                </div>
              ) : null}
              <div
                style={{
                  marginTop: "16px",
                  paddingTop: "16px",
                  borderTop: "1px solid #e2e8f0",
                }}
              >
                <p style={{ margin: "0", fontSize: "14px", color: "#64748b" }}>
                  <strong>Offer status:</strong> Active
                </p>
              </div>
            </div>
            <div className="special-offer-actions">
              <button
                className="accept-btn"
                onClick={() => {
                  setOfferModal({ isOpen: false, offer: null });
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step1;
